<svelte:options runes={true} />

<script lang="ts">
  import { getTitle } from '$lib/components/title'
  import Container from '$lib/site/Container.svelte'
</script>

<svelte:head>
  <title>{getTitle('Terms of Service')}</title>
</svelte:head>

<Container nav padding footer>
  <div class="container prose">
    <h1>Terms of Service</h1>
    <p><strong>Effective Date:</strong> 7 June 2023</p>
    <p>
      When we say “Company”, “we”, “our”, or “us” in this document, we are referring to Turbomaps
      Limited.
    </p>
    <p>
      When we say “Services”, we mean our website - <a href="https://turbomaps.io">turbomaps.io</a> and
      any product created and maintained by Turbomaps Limited.
    </p>
    <p>
      When we say “You” or “your”, we are referring to the people or organizations that own an
      account with one or more of our Services.
    </p>
    <p>
      We may update these Terms of Service (”Terms”) in the future. Whenever we make a significant
      change to our policies, we will refresh the date at the top of this page and take any other
      appropriate steps to notify account holders.
    </p>
    <p>
      When you use our Services, now or in the future, you are agreeing to the latest Terms. There
      may be times where we do not exercise or enforce a right or provision of the Terms; however,
      that does not mean we are waiving that right or provision.
      <strong>These Terms do contain a limitation of our liability.</strong> If you violate any of the
      Terms, we may terminate your account.
    </p>
    <h2>Account Terms</h2>
    <ol>
      <li>
        You are responsible for maintaining the security of your account and password and for
        ensuring that any of your users do the same. The Company cannot and will not be liable for
        any loss or damage from your failure to comply with this security obligation. We recommend
        all users set up two-factor authentication for added security.
      </li>
      <li>
        You are responsible for all content posted to and activity that occurs under your account,
        including content posted by and activity of any users in your account.
      </li>
      <li>
        You must be a human. Accounts registered by “bots” or other automated methods are not
        permitted.
      </li>
    </ol>
    <h2>Payment, Refunds, and Plan Changes</h2>
    <p>
      Our Services are currently free. We do not ask you for your credit card and we do not sell
      your data.
    </p>
    <h2>Modifications to the Service and Prices</h2>
    <ol>
      <li>
        We reserve the right at any time to modify or discontinue, temporarily or permanently, any
        part of our Services with or without notice.
      </li>
      <li>
        We may change the pricing structure for our products. If we do so, we will give at least 30
        days notice and will notify you via the email address on record. We may also post a notice
        about changes on our websites or the affected Services themselves.
      </li>
    </ol>
    <h2>Cancellation and Termination</h2>
    <ol>
      <li>
        You are solely responsible for properly canceling your account. Within each of our Services,
        we provide a simple cancellation link. An email request to cancel your account is not
        automatically considered cancellation. If you need help canceling your account, you can
        always contact our Support team.
      </li>
      <li>
        All of your content will be inaccessible from the Services immediately upon account
        cancellation. Within 30 days, all content will be permanently deleted from active systems
        and logs. Within 90 days, all content will be permanently deleted from our backups. We
        cannot recover this information once it has been permanently deleted.
      </li>
      <li>
        We have the right to suspend or terminate your account and refuse any and all current or
        future use of our Services for any reason at any time. Suspension means you and any other
        users on your account will not be able to access the account or any content in the account.
        Termination will furthermore result in the deletion of your account or your access to your
        account, and the forfeiture and relinquishment of all content in your account. We also
        reserve the right to refuse the use of the Services to anyone for any reason at any time.
      </li>
      <li>
        Verbal, physical, written or other abuse (including threats of abuse or retribution) of a
        Company employee or officer will result in immediate account termination.
      </li>
    </ol>
    <h2>Uptime, Security, and Privacy</h2>
    <ol>
      <li>
        Your use of the Services is at your sole risk. We provide these Services on an “as is” and
        “as available” basis. We do not offer service-level agreements (SLAs) for our Services.
      </li>
      <li>
        We reserve the right to temporarily disable your account if your usage significantly exceeds
        the average usage of other customers of the Services. Of course, we’ll reach out to the
        account owner before taking any action except in rare cases where the level of use may
        negatively impact the performance of the Service for other customers.
      </li>
      <li>
        We take many measures to protect and secure your data through backups, redundancies, and
        encryption. We enforce encryption for all data in transit and at rest.
      </li>
      <li>
        When you use our Services, you entrust us with your data. You agree that Turbomaps Limited
        may process your data as described in our
        <a href="/privacy">Privacy Notice</a> and for no other purpose. We as humans can access your
        data for the following reasons:
        <ul>
          <li>
            <strong>To help you with support requests you make.</strong> We’ll ask for express consent
            before accessing your account.
          </li>
          <li>
            <strong
              >On the rare occasions when an error occurs that stops an automated process partway
              through.</strong
            >
            We get automated alerts when such errors occur. When we can fix the issue and restart automated
            processing without looking at any personal data, we do. In rare cases, we have to look at
            a minimum amount of personal data to fix the issue. In these rare cases, we aim to fix the
            root cause to prevent the errors from recurring.
          </li>
          <li>
            <strong>To safeguard Turbomaps.</strong> We’ll look at logs and metadata as part of our work
            to ensure the security of your data and the Services as a whole.
          </li>
          <li>
            <strong>To the extent required by applicable law.</strong> As a New Zealand company with data
            infrastructure located in various jurisdictions including New Zealand, Singapore, Europe,
            and the United States, we only preserve or share customer data if compelled by a government
            authority with a legally binding order or proper request, or in limited circumstances in the
            event of an emergency request. If Turbomaps is audited by a tax authority, we only share the
            bare minimum billing information needed to complete the audit.
          </li>
        </ul>
      </li>
      <li>
        We use third party vendors and hosting partners to provide the necessary hardware, software,
        networking, storage, and related technology required to run the Services.
      </li>
      <li>
        Turbomaps is a “service provider”, not a “business” or “third party”, with respect to your
        use of the Services. That means we process any data you share with us only for the purpose
        you signed up for and as described in these Terms, and our
        <a href="/privacy">Privacy Notice</a>. We do not retain, use, disclose, or sell any of that
        information for any other commercial purposes unless we have your explicit permission.
      </li>
    </ol>
    <h2>Copyright and Content Ownership</h2>
    <ol>
      <li>
        All content posted on the Services must comply with New Zealand copyright law. You can
        notify us of either copyright infringement claims or counter-notifications via email:
        hello@turbomaps.io.
      </li>
      <li>
        You give us a limited license to use the content posted by you and your users in order to
        provide the Services to you, but we claim no ownership rights over those materials. All
        materials you submit to the Services remain yours.
      </li>
      <li>
        We do not pre-screen content, but we reserve the right (but not the obligation) in our sole
        discretion to refuse or remove any content that is available via the Service.
      </li>
      <li>
        The Company or its licensors own all right, title, and interest in and to the Services,
        including all intellectual property rights therein, and you obtain no ownership rights in
        the Services as a result of your use. You may not duplicate, copy, or reuse any portion of
        the HTML, CSS, JavaScript, or visual design elements without express written permission from
        the Company. You must request permission to use the Company’s logos or any Service logos for
        promotional purposes. Please contact us for requests to use logos. We reserve the right to
        rescind any permissions if you violate these Terms.
      </li>
      <li>
        You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion of the
        Services, use of the Services, or access to the Services without the express written
        permission of the Company.
      </li>
    </ol>
    <h2>Features and Bugs</h2>
    <p>
      We design our Services with care, based on our own experience and the experiences of customers
      who share their time and feedback. However, there is no such thing as a service that pleases
      everybody. We make no guarantees that our Services will meet your specific requirements or
      expectations.
    </p>
    <p>
      We also test all of our features extensively before shipping them. As with any software, our
      Services inevitably have some bugs. We track the bugs reported to us and work through priority
      ones, especially any related to security or privacy. Not all reported bugs will get fixed and
      we don’t guarantee completely error-free Services.
    </p>
    <h2>Liability</h2>
    <p>
      <em
        ><strong
          >You expressly understand and agree that the Company shall not be liable, in law or in
          equity, to you or to any third party for any direct, indirect, incidental, lost profits,
          special, consequential, punitive or exemplary damages, including, but not limited to,
          damages for loss of profits, goodwill, use, data or other intangible losses (even if the
          Company has been advised of the possibility of such damages), resulting from: (i) the use
          or the inability to use the Services; (ii) the cost of procurement of substitute goods and
          services resulting from any goods, data, information or services purchased or obtained or
          messages received or transactions entered into through or from the Services; (iii)
          unauthorized access to or alteration of your transmissions or data; (iv) statements or
          conduct of any third party on the service; (v) or any other matter relating to these Terms
          or the Services, whether as a breach of contract, tort (including negligence whether
          active or passive), or any other theory of liability.</strong
        ></em
      >
    </p>
    <p>If you have a question about any of these Terms, please contact us.</p>
    <h2>How to contact us</h2>
    <p>
      For more information about our terms of service, or if you have any questions, please contact
      us by email at hello@turbomaps.io.
    </p>
    <h2>Third Party Licenses</h2>
    <p>
      Turbomaps uses a number of third party packages. The software licenses for these packages can
      be found at <a href="/LICENSES.txt">LICENSES.txt</a>.
    </p>
  </div>
</Container>
